import { startTransition, useEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { paginaEsterna } from '../../lib/ruoliEsterni';
import { useUIStore } from '../../stores/ui.store';
import { Icon } from '../ui/Icon';
import { NAV_GROUPS, type NavEntry } from '../../lib/nav';
import { useQuery } from '@tanstack/react-query';
import { useActionStream } from '../../features/oggi/useActionStream';
import { tasksSummaryQuery } from '../../features/tasks/tasksApi';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import { cn } from '../../lib/utils';
import { isFrozen } from '../../lib/frozen';
import { ADMIN_PAGES, canSeeAdminPage, type AdminPageKey } from '../../lib/adminRoutes';
import { adminOverviewQuery, daysFromToday } from '../../features/admin/adminApi';
import { useSidebarPill } from './useSidebarPill';

/**
 * Esegue `fn` appena il browser ha disegnato il prossimo fotogramma: il tempo
 * di consegnare al compositor l'animazione della pill, prima che la pagina
 * nuova occupi il main thread. Il timeout e' la rete se rAF non arriva.
 */
function afterNextPaint(fn: () => void) {
  let done = false;
  const run = () => { if (!done) { done = true; fn(); } };
  requestAnimationFrame(() => setTimeout(run, 0));
  setTimeout(run, 100);
}

/**
 * Le sezioni stanno in lib/nav.ts (NAV_GROUPS), la stessa lista del telefono e
 * del percorso in alto: una sezione nuova si aggiunge lì e compare qui da sola.
 * Qui si aggiungono solo le sottovoci dell'Amministrazione, che vengono da
 * lib/adminRoutes.ts e si aprono solo dentro /admin.
 */
const NAV = NAV_GROUPS.map((g) => ({
  title: g.title,
  items: g.items.map((it) => ({
    ...it,
    count: undefined as number | undefined,
    sub: it.adminSub
      ? ADMIN_PAGES.filter((p) => !p.nascosta).map((p) => ({ key: p.key, to: p.to, label: p.label, icon: p.icon, module: undefined as ModuleKey | undefined, div: undefined as NavEntry['div'] }))
      : undefined,
  })),
}));

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  // Agenti e professionisti (persone esterne) vedono solo la loro pagina: una voce sola.
  const esterna = paginaEsterna(user?.role.key);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const canvas = useUIStore((s) => s.canvas);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const glassRef = useRef<HTMLDivElement>(null);

  // Al clic la sidebar si muove SUBITO verso la voce scelta (pill e sottovoci),
  // e la pagina nuova parte un fotogramma dopo, in una transition: cosi' il
  // suo render non ruba i fotogrammi dell'animazione. Finche' React non ha
  // raggiunto l'indirizzo nuovo, la voce attiva e' quella cliccata.
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const activePath = pendingPath ?? location.pathname;
  useEffect(() => {
    // Arrivati (o reindirizzati altrove): torna a comandare l'indirizzo vero
    setPendingPath((p) => (p != null && (location.pathname === p || location.pathname === window.location.pathname) ? null : p));
  }, [location]);
  const go = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    // Come fa Link: stessa pagina = replace, niente voce doppia nella cronologia
    const replace = location.pathname + location.search + location.hash === to;
    setPendingPath(to);
    afterNextPaint(() => startTransition(() => navigate(to, { replace })));
  };

  useSidebarPill(navRef, pillRef, glassRef, activePath, collapsed ? 'collapsed' : 'expanded');

  // Su una finestra bassa le voci possono non starci tutte: la voce attiva non
  // deve restare nascosta sotto la scheda utente (su /settings a 800px di
  // altezza la pastiglia non si vedeva). Si aspetta che le sottovoci finiscano
  // di aprirsi, poi si porta in vista l'ultima voce accesa.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const t = window.setTimeout(() => {
      if (nav.scrollHeight <= nav.clientHeight + 1) return;
      const accese = nav.querySelectorAll<HTMLElement>('.sb-item[aria-current="true"]');
      const el = accese[accese.length - 1];
      if (!el) return;
      const n = nav.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (r.bottom > n.bottom - 8) nav.scrollTop += r.bottom - n.bottom + 8;
      else if (r.top < n.top + 8) nav.scrollTop -= n.top + 8 - r.top;
    }, 460);
    return () => window.clearTimeout(t);
  }, [activePath, collapsed]);
  // Conteggio vivo per "Oggi" (critico + oggi) — stessa cache dello stream, zero fetch extra
  const { counts } = useActionStream();
  const oggiCount = counts.critical + counts.today;

  const canSee = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  };

  // Badge "Task" = scadono oggi + in ritardo dei MIEI task (stessa query dello
  // stream Oggi: cache condivisa, un fetch al minuto al massimo)
  const { data: taskSummary } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canSee(ModuleKey.TASKS) });
  const tasksCount = (taskSummary?.dueToday ?? 0) + (taskSummary?.overdue ?? 0);

  // Pallini delle sottovoci dell'Amministrazione: solo conteggi (mai importi)
  // e solo dove c'è qualcosa da fare. Stessa cache dello stream di Oggi, che
  // l'overview la chiede già: zero fetch in più.
  const { data: adminOv } = useQuery({ ...adminOverviewQuery, enabled: canSee(ModuleKey.AMMINISTRAZIONE) });
  const adminBadge: Partial<Record<AdminPageKey, number>> = {
    fatture: adminOv?.fatture.overdueCount ?? 0,
    banca: adminOv?.fatture.daRiconciliare?.proposte ?? adminOv?.fatture.toReview.length ?? 0,
    // Scadenze fiscali da oggi ai prossimi 7 giorni
    fisco: (adminOv?.tasse.next ?? []).filter((t) => { const d = daysFromToday(t.dueDate); return d != null && d >= 0 && d <= 7; }).length,
  };

  return (
    <aside className="os-sidebar flex flex-col">
      <div className="sb-brand">
        {collapsed ? (
          <div className="sb-brand-mark" style={{ width: 'auto', padding: '0 10px' }}>
            <img src="/logos/m-white.svg" alt="Masamune" style={{ height: 14, width: 'auto', display: 'block' }} />
          </div>
        ) : (
          <>
            {/* Spina viola: sempre il logotipo bianco (quadratino viola + scritta bianca) */}
            <img src="/logos/masamune.svg" alt="Masamune" className="sb-brand-logo" />
            <div className="sb-brand-tag">OS v0.1</div>
          </>
        )}
      </div>

      {/* overflow-x-hidden: con overflow-y auto anche l'asse x diventa auto, e le sottovoci che entrano
          scorrendo di lato (o la gelatina della pill) facevano comparire una barra orizzontale (24/9). */}
      <div ref={navRef} className="flex-1 py-1 overflow-y-auto overflow-x-hidden relative">
        {/* Pill attiva che scivola tra le voci (posizione e misure: useSidebarPill); il vetro (figlio) è quello che "gelatina" */}
        <div ref={pillRef} className="sb-active-pill" aria-hidden="true">
          <div ref={glassRef} className="sb-active-pill-glass" />
        </div>
        {/* Agenti e professionisti hanno una voce sola, la loro pagina, che dice cos'è per loro. */}
        {esterna && (
          <div className="sb-section">
            <Link
              to={esterna.to}
              onClick={go(esterna.to)}
              className="sb-item"
              aria-current={activePath === esterna.to ? 'true' : 'false'}
              title={collapsed ? esterna.label : undefined}
            >
              <Icon name={esterna.icon} size={16} />
              {!collapsed && <span>{esterna.label}</span>}
            </Link>
          </div>
        )}
        {!esterna && NAV.map((group) => {
          const visible = group.items.filter((it) => canSee(it.module));
          if (!visible.length) return null;
          return (
            <div className="sb-section" key={group.title}>
              {!collapsed && <div className="sb-section-label">{group.title}</div>}
              {visible.map((item) => {
                const isActive = activePath === item.to || (item.to !== '/' && activePath.startsWith(item.to + '/'));
                // Fase 0: la voce resta visibile e cliccabile (porta alla pagina
                // di pausa), ma smorzata e con badge "In pausa" al posto del conteggio
                const paused = isFrozen(item.module);
                return (
                  <div key={item.key}>
                    {/* Link e non NavLink: NavLink decide aria-current sulla location di
                        React, che durante la transition e' ancora quella vecchia */}
                    <Link
                      to={item.to}
                      onClick={go(item.to)}
                      className="sb-item"
                      data-div={item.div}
                      aria-current={isActive ? 'true' : 'false'}
                      aria-disabled={paused || undefined}
                      title={collapsed ? (paused ? `${item.label} (in pausa)` : item.label) : undefined}
                    >
                      <Icon name={item.icon} size={16} />
                      {!collapsed && (
                        <>
                          <span>{item.label}</span>
                          {paused
                            ? <span className="sb-count sb-count-paused">In pausa</span>
                            : item.key === 'oggi' && oggiCount > 0
                              ? <span className="sb-count">{oggiCount}</span>
                              : item.key === 'tasks' && tasksCount > 0
                                ? <span className="sb-count">{tasksCount}</span>
                                : item.count != null && <span className="sb-count">{item.count}</span>}
                        </>
                      )}
                    </Link>
                    {/* Le sottovoci si aprono solo dentro la loro sezione. Restano
                        montate e si aprono/chiudono con l'accordion (.sb-sub): da
                        chiuse sono alte 0 e invisibili anche alla tastiera. */}
                    {item.sub && !collapsed && (
                      <div className="sb-sub" data-sb-sub="" data-open={isActive ? 'true' : 'false'}>
                        <div className="sb-sub-inner">
                          <div className="sb-sub-list ml-6 pl-2 border-l border-line">
                            {item.sub
                              // Dipendenti chiede il livello ADMIN, non basta leggere
                              .filter((s) => item.key !== 'admin' || canSeeAdminPage(user, s.key as AdminPageKey))
                              .map((s, i) => {
                              const subPaused = isFrozen(s.module ?? item.module);
                              const badge = item.key === 'admin' ? adminBadge[s.key as AdminPageKey] ?? 0 : 0;
                              return (
                                <Link
                                  key={s.key}
                                  to={s.to}
                                  onClick={go(s.to)}
                                  className="sb-item sb-subitem"
                                  style={{ '--i': i } as CSSProperties}
                                  data-div={s.div}
                                  aria-current={isActive && activePath === s.to ? 'true' : 'false'}
                                  aria-disabled={subPaused || undefined}
                                  tabIndex={isActive ? undefined : -1}
                                >
                                  <Icon name={s.icon} size={14} />
                                  <span>{s.label}</span>
                                  {subPaused
                                    ? <span className="sb-count sb-count-paused">In pausa</span>
                                    : badge > 0 && <span className="sb-count">{badge}</span>}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {user && (
        <div className="sb-user">
          <div className="sb-avatar">{user.initials}</div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-text text-[13px] truncate">{user.firstName} {user.lastName}</div>
              <span className="sb-role">{user.role.name}</span>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
